import { defineStore } from "pinia";
import {
    AddDiscountOfferCommand,
    ApiClient, ApiError, CancelablePromise, CancelDiscountOffersCommand, CompanyDashboardDataModel,
    DiscountOfferStatus, InvoiceStatus, InvoiceStatusEventDto, InvoicesVM,
    InvoiceVM, SendDiscountOffersCommand, UpdateDiscountOfferCommand
} from "../../../clients/ApiClient";
import { PropType } from "vue";
import { api } from "@/clients/ApiClientService";
import router from "@/router";
import { errorToast, successToast } from "@/helpers/customToast";
// import {} from "@/extensions/cancelablePromiseExtension";


export const useInvoiceStore = defineStore('invoiceStore', {
    state: () => {
        return {
            invoiceState: Object as Partial<InvoiceVM>,
            companyDashboardState: Object as Partial<CompanyDashboardDataModel>,
            invoiceFilteredByVendorAndYearState: Object as Partial<InvoicesVM>,
            canAddOffer: false,
            canEditOffer: false,
            isEditOffer: false,
            isAddOfferFromIndex: false,
            editOfferId: '',
        }
    },
    actions: {
        async getInvoice(invoiceId: string | null | undefined): Promise<boolean> {
            if (!invoiceId) {
                this.$state.invoiceState = {} as Partial<InvoiceVM>;
                this.$state.canAddOffer = false;
                return false;
            }
            const res = await api.invoices.get(invoiceId).resolve();
            if (res.result === undefined) {
                this.$state.invoiceState = {} as Partial<InvoiceVM>;
                this.$state.canAddOffer = false;
                return false;
            }

            this.$state.invoiceState = res.result
            const offers = this.$state.invoiceState.InvoiceDiscountOffers;
            this.$state.canAddOffer = (offers ?? []).every(e => {
                return (e.Status === DiscountOfferStatus.CANCELLED || e.Status === DiscountOfferStatus.REJECTED || e.Status === DiscountOfferStatus.EXPIRED);
            });
            this.$state.canAddOffer = this.$state.canAddOffer && (this.$state.invoiceState.Status !== InvoiceStatus.PAID);
            this.$state.canAddOffer = this.$state.canAddOffer && (Date.parse(this.$state.invoiceState.DueDate ?? '01 Jan 1970 00:00:00 GMT') > Date.now());
            return true;
        },

        async getInvoiceByVendorAndYear(vendorId: string, year: number) {
            const res = await api.invoices.getInvoicesByVendorAndYear(vendorId, year).resolve();
            if (res.result !== undefined) {
                this.$state.invoiceFilteredByVendorAndYearState = res.result;
            }
        },

        async addInvoiceOffer(model: AddDiscountOfferCommand): Promise<boolean> {
            try {
                await api.invoices.addDiscountOffer(model);
                successToast()
                await this.getInvoice(model.InvoiceId);
                return true;
            } catch (error) {
                errorToast(getInvoiceOfferErrorMessage(error));
                return false;
            }
        },


        async addInvoiceOfferAndSend(model: AddDiscountOfferCommand): Promise<boolean> {
            let offerId: string;
            try {
                const addedOffer = await api.invoices.addDiscountOffer(model);
                if (!addedOffer.Id) {
                    errorToast();
                    return false;
                }
                offerId = addedOffer.Id;
            } catch (error) {
                errorToast(getInvoiceOfferErrorMessage(error));
                return false;
            }

            try {
                await api.invoices.sendDiscountOffers({
                    SentOfferId: offerId,
                    InvoiceId: model.InvoiceId,
                });
                await this.getInvoice(model.InvoiceId);
                return true;
            } catch (error) {
                // The first request already persisted the Draft. Refresh so the invoice
                // page exposes its Send action and make that partial success explicit.
                await this.getInvoice(model.InvoiceId);
                errorToast(
                    `The offer was saved as Draft, but it could not be sent. ${getInvoiceOfferErrorMessage(error)}`);
                return false;
            }
        },


        async updateInvoiceOffer(model: UpdateDiscountOfferCommand): Promise<boolean> {
            try {
                await api.invoices.updateDiscountOffer(model);
                await this.getInvoice(model.InvoiceId);
                successToast()
                return true;
            } catch (error) {
                errorToast(getInvoiceOfferErrorMessage(error));
                return false;
            }
        },

        async cancelInvoiceOffer<bool>(model: CancelDiscountOffersCommand) {
            const res = await api.invoices.cancelDiscountOffers(model).resolve();
            if (res.succeed) {
                await this.getInvoice(model.InvoiceId);
                successToast()
            } else {
                errorToast();
            }
            return res.succeed;
        },


        async sendOfferForApproval<bool>(model: SendDiscountOffersCommand) {
            const res = await api.invoices.sendDiscountOffers(model).resolve();
            if (res.succeed) {
                await this.getInvoice(this.$state.invoiceState.Id);
            }
            // else{
            //     errorToast();
            // }
            return res.succeed
        },


        async getCompanyDashboardData<CompanyDashboardDataModel>() {
            const res = await api.invoices.getCompanyDashboardData(localStorage.getItem('company_ID')!).resolve();
            if (res.succeed && res.result !== undefined) {
                this.$state.companyDashboardState = res.result
            }
            return res.succeed
        },

        setUpdateOfferData(offerId: string) {
            this.$state.isEditOffer = true;
            this.$state.isAddOfferFromIndex = true;
            this.$state.editOfferId = offerId;
        },
        setDataFromIndex() {
            this.$state.isAddOfferFromIndex = true;
        },
        restoreUpdateOfferData() {
            this.$state.isEditOffer = false;
            this.$state.isAddOfferFromIndex = false;
            this.$state.editOfferId = '';
        },
    },

    getters: {
        invoice: (state) => state,
    }
});

function getInvoiceOfferErrorMessage(error: unknown): string {
    const apiError = error as ApiError;
    if (typeof apiError.body === "string" && apiError.body.trim()) {
        return apiError.body;
    }

    if (typeof apiError.body?.message === "string" && apiError.body.message.trim()) {
        return apiError.body.message;
    }

    return "Please try again or contact support.";
}
